/**
 * "The machine just woke up", told to the page by the desktop shell.
 *
 * WHY THE SHELL HAS TO SAY IT — the page's own recovery triggers are `visibilitychange` and
 * `online` (see room-connection.ts). Neither fires when a laptop resumes with the Niobe window
 * already visible and the network already up: nothing about the DOM changed, so the room stays
 * broken until the member happens to switch away and back. The main process CAN see it —
 * Electron's powerMonitor emits 'resume' and 'unlock-screen' — so it forwards the fact here.
 *
 * A no-op in the browser, where `window.niobeDesktop` is absent: the subscription simply never
 * fires and the existing two triggers remain the whole story.
 */

interface WakeBridge {
  /** Subscribe; returns an unsubscribe function. */
  onWake?(cb: () => void): () => void;
}

const NOOP = (): void => {};

export function onDesktopWake(cb: () => void): () => void {
  const bridge = (window as { niobeDesktop?: WakeBridge }).niobeDesktop;
  // Optional on purpose: an older shell around a newer SPA (the app and the bundle update
  // independently) simply has no onWake, and must not throw its way through the recovery effect.
  if (typeof bridge?.onWake !== 'function') return NOOP;
  try {
    return bridge.onWake(cb);
  } catch {
    return NOOP;
  }
}

/** What the shell reports about its window: out of sight (tray, minimised) or back on screen. */
export type DesktopVisibility = 'hidden' | 'shown'

interface VisibilityBridge {
  /** Subscribe; returns an unsubscribe function. */
  onVisibility?(cb: (state: DesktopVisibility) => void): () => void
}

/**
 * "The window was hidden / shown", told to the page by the desktop shell.
 *
 * WHY THE SHELL HAS TO SAY IT — the desktop window runs with `backgroundThrottling: false` (its
 * CRDT keepalives need it), and one side effect is that `document.visibilityState` stays 'visible'
 * while the window sits in the tray. So `visibilitychange` never fires, and nothing in the page can
 * tell it is out of sight. The shell can, and forwards it (client/desktop/main.js#sendVisibility).
 *
 * A no-op in the browser, and on an older shell that has no `onVisibility`, like `onDesktopWake`.
 */
interface NavigateBridge {
  /** Subscribe; returns an unsubscribe function. */
  onNavigate?(cb: (path: string) => void): () => void
}

/**
 * "Go to this path", told to the page by the desktop shell when a `niobe://` deep link opened the
 * app (client/desktop/niobe-deep-link.js). The shell already validated it against the route shapes
 * this SPA accepts; the page still takes nothing but a same-origin path. Following it with the
 * router's own navigate() keeps the session and the CRDT rooms alive — no reload.
 *
 * Subscribing is also how the shell learns the page is ready: it holds a link until then. A no-op in
 * the browser, and on an older shell that has no `onNavigate`, like `onDesktopWake`.
 */
export function onDesktopNavigate(cb: (path: string) => void): () => void {
  const bridge = (window as { niobeDesktop?: NavigateBridge }).niobeDesktop
  if (typeof bridge?.onNavigate !== 'function') return NOOP
  try {
    return bridge.onNavigate((path) => {
      if (typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')) cb(path)
    })
  } catch {
    return NOOP
  }
}

export function onDesktopVisibility(cb: (state: DesktopVisibility) => void): () => void {
  const bridge = (window as { niobeDesktop?: VisibilityBridge }).niobeDesktop
  if (typeof bridge?.onVisibility !== 'function') return NOOP
  try {
    return bridge.onVisibility((state) => {
      if (state === 'hidden' || state === 'shown') cb(state)
    })
  } catch {
    return NOOP
  }
}
